import type { Terminal } from '../terminal';
import type { GameState, Strike, StrikeAssignment } from '../../types/game-state';
import { getReadyWeapons } from '../../engine/arsenal';

function formatPopulation(pop: number): string {
  if (pop >= 1_000_000) return `${(pop / 1_000_000).toFixed(1)}M`;
  if (pop >= 1_000) return `${(pop / 1_000).toFixed(0)}K`;
  return String(pop);
}

async function runTargeting(terminal: Terminal, state: GameState): Promise<Strike> {
  const playerArsenal = state.playerSide === 'US' ? state.usArsenal : state.ussrArsenal;
  const readyWeapons = getReadyWeapons(playerArsenal);

  const enemyCities = state.playerSide === 'US'
    ? state.world.ussrCities.filter(c => !c.isDestroyed)
    : state.world.usCities.filter(c => !c.isDestroyed);

  const enemyInstallations = state.playerSide === 'US'
    ? state.world.ussrInstallations.filter(i => !i.isDestroyed)
    : state.world.usInstallations.filter(i => !i.isDestroyed);

  await terminal.println('');
  await terminal.println('════════════════════════════════════════════════════════════════');
  await terminal.println('  TARGET SELECTION', { cssClass: 'bright' });
  await terminal.println('════════════════════════════════════════════════════════════════');
  await terminal.println('');
  await terminal.println(`  AVAILABLE WEAPONS: ${readyWeapons.length}`, { cssClass: 'bright' });
  await terminal.println('');

  // List cities
  await terminal.println('  ── POPULATION CENTERS ──', { cssClass: 'danger' });
  enemyCities.forEach((city, i) => {
    terminal.printImmediate(`    C${String(i + 1).padStart(2, '0')}  ${city.name.padEnd(22)} POP: ${formatPopulation(city.currentPopulation).padStart(6)}\n`);
  });

  await terminal.println('');

  // List military installations
  await terminal.println('  ── MILITARY INSTALLATIONS ──', { cssClass: 'warning' });
  enemyInstallations.forEach((inst, i) => {
    const typeLabel = inst.type.replace(/_/g, ' ').toUpperCase();
    terminal.printImmediate(`    M${String(i + 1).padStart(2, '0')}  ${inst.name.padEnd(22)} TYPE: ${typeLabel}\n`);
  });

  await terminal.println('');
  await terminal.println('  COMMANDS: [target code] to add, DONE to launch, AUTO for automatic');
  await terminal.println('');

  const assignments: StrikeAssignment[] = [];
  let weaponIndex = 0;

  while (weaponIndex < readyWeapons.length) {
    const weapon = readyWeapons[weaponIndex];
    if (!weapon) break;

    const input = await terminal.waitForInput(`TARGET (${readyWeapons.length - weaponIndex} weapons remain)> `);
    const upper = input.toUpperCase().trim();

    if (upper === 'DONE' || upper === 'LAUNCH') {
      if (assignments.length === 0) {
        await terminal.println('NO TARGETS ASSIGNED. SELECT AT LEAST ONE TARGET.', { cssClass: 'warning' });
        continue;
      }
      break;
    }

    if (upper === 'AUTO') {
      await terminal.println('AUTOMATIC TARGETING ENGAGED...', { cssClass: 'warning' });
      await terminal.pause(500);

      // Auto-target: mix of installations and cities
      const remainingWeapons = readyWeapons.slice(weaponIndex);
      const halfPoint = Math.ceil(remainingWeapons.length / 2);

      // First half on military
      for (let i = 0; i < halfPoint && i < remainingWeapons.length; i++) {
        const w = remainingWeapons[i];
        const target = enemyInstallations[i % enemyInstallations.length];
        if (w && target) {
          assignments.push({
            weaponId: w.id,
            targetId: target.id,
            targetType: 'installation',
          });
        }
      }

      // Second half on cities by population
      const sortedCities = [...enemyCities].sort((a, b) => b.currentPopulation - a.currentPopulation);
      for (let i = halfPoint; i < remainingWeapons.length; i++) {
        const w = remainingWeapons[i];
        const cityIdx = (i - halfPoint) % sortedCities.length;
        const target = sortedCities[cityIdx];
        if (w && target) {
          assignments.push({
            weaponId: w.id,
            targetId: target.id,
            targetType: 'city',
          });
        }
      }

      await terminal.println(`${remainingWeapons.length} WEAPONS AUTO-ASSIGNED.`, { cssClass: 'bright' });
      break;
    }

    // Parse target code (C01, M01, etc.)
    const cityMatch = upper.match(/^C(\d+)$/);
    const installMatch = upper.match(/^M(\d+)$/);

    if (cityMatch) {
      const idx = parseInt(cityMatch[1] ?? '0', 10) - 1;
      const target = enemyCities[idx];
      if (target) {
        assignments.push({
          weaponId: weapon.id,
          targetId: target.id,
          targetType: 'city',
        });
        await terminal.println(`  >> ${weapon.system.name} → ${target.name}`, { cssClass: 'danger' });
        weaponIndex++;
      } else {
        await terminal.println('INVALID TARGET CODE.', { cssClass: 'warning' });
      }
    } else if (installMatch) {
      const idx = parseInt(installMatch[1] ?? '0', 10) - 1;
      const target = enemyInstallations[idx];
      if (target) {
        assignments.push({
          weaponId: weapon.id,
          targetId: target.id,
          targetType: 'installation',
        });
        await terminal.println(`  >> ${weapon.system.name} → ${target.name}`, { cssClass: 'warning' });
        weaponIndex++;
      } else {
        await terminal.println('INVALID TARGET CODE.', { cssClass: 'warning' });
      }
    } else {
      await terminal.println('USE C## FOR CITY, M## FOR MILITARY, AUTO, OR DONE.', { cssClass: 'dim' });
    }
  }

  await terminal.println('');
  await terminal.println(`STRIKE PACKAGE: ${assignments.length} WEAPONS ASSIGNED TO ${new Set(assignments.map(a => a.targetId)).size} TARGETS`, { cssClass: 'bright' });
  await terminal.println('');

  return {
    attackerSide: state.playerSide,
    round: state.currentRound,
    assignments,
  };
}

export { runTargeting };
